import React, {
    Fragment,
    useState,
    useEffect,
    useCallback,
    useContext,
    useRef,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import { apiDel, apiGet, apiPut, messageFromException } from "helpers/Request";
import { modal } from "components/HeadlessModal";
import { useAuth } from "hooks/Auth";
import { toast } from "react-hot-toast";
import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import { transform as containerTransformer } from "schemas/Container";
import { getPaginationMeta } from "schemas/Util";
import ContainerTable from "components/DesignSystem/ContainerTable";
import {
    TrashIcon,
    CloudDownloadIcon,
    CloudUploadIcon,
    RefreshIcon,
    SupportIcon,
    SearchIcon,
} from "@heroicons/react/outline";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import Pagination from "components/General/Pagination";
import ButtonGroup from "components/Buttons/ButtonGroup";
import { SQL_DATE_TIME_FORMAT, fromNow } from "helpers/Date";
import { useContextualHelp } from "hooks/Help";
import { AppContext } from "context/App";
import { useAsyncError } from "hooks/AsyncError";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";

export default function ContainerScreen() {
    // load hooks
    const auth = useAuth();
    const location = useLocation();
    const history = useHistory();
    const throwError = useAsyncError();
    const contextualHelp = useContextualHelp();
    const { setCurrentNav } = useContext(AppContext);
    const inputRef = useRef();

    // set state
    const [loading, setLoading] = useState(true);
    const [containerItems, setContainerItems] = useState([]);
    const [paginationMeta, setPaginationMeta] = useState({});
    const [selectedItems, setSelectedItems] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [toolbarButtonsEnabled, setToolbarButtonsEnabled] = useState(false);
    const [queryParams, setQueryParams] = useState(
        new URLSearchParams(location.search)
    );

    const filterItems = {
        status: [
            {
                label: "Draft",
                value: "draft",
            },
            {
                label: "Published",
                value: "published",
            },
        ],
        type: [
            {
                label: "Automatic",
                value: "automatic",
            },
            {
                label: "Manual",
                value: "manual",
            },
        ],
    };

    const loadContainers = useCallback(() => {
        setLoading(true);
        setSelectedItems([]);
        const defaultParams = {
            per_page: 20,
            dir: "asc",
            order: "title",
            type: ["automatic", "manual"],
        };
        const definedParams = Object.fromEntries(
            new URLSearchParams(location.search)
        );

        apiGet(
            "/v4/container",
            auth.publication,
            Object.assign({}, defaultParams, definedParams)
        )
            .then((res) => {
                setContainerItems(containerTransformer(res));
                setPaginationMeta(getPaginationMeta(res));
                setLoading(false);
            })
            .catch((err) => {
                if (err.message === "failed refresh") {
                    throwError(new Error("Failed to refresh auth token"));
                    return;
                }
                throwError(new Error("Failed to load container."));
            });
    }, [location, auth.publication, throwError]);

    const updateParams = (key, val = null) => {
        const params = new URLSearchParams(location.search);

        if (typeof key === "object") {
            Object.entries(key).forEach(([k, v]) => {
                params.set(k, v);
            });
        } else {
            if (params.has(key) && params.get(key) === val) {
                params.delete(key);
            } else {
                params.set(key, val);
            }
        }

        history.replace(location.pathname + "?" + params.toString());
    };

    const onResetFilters = () => {
        history.replace(location.pathname + "?");
    };

    const onSortClick = (sortKey) => {
        let sortDir = "desc";
        if (queryParams.has("order") && queryParams.get("order") === sortKey) {
            if (queryParams.has("dir") && queryParams.get("dir") === "asc") {
                sortDir = "desc";
            } else {
                sortDir = "asc";
            }
        }

        updateParams({ order: sortKey, dir: sortDir });
    };

    const onSingleSelect = (item, checked) => {
        if (checked) {
            setSelectedItems([...selectedItems, item.uuid]);
        } else {
            setSelectedItems(selectedItems.filter((i) => i !== item.uuid));
        }
    };
    const onAllSelect = (items) => {
        setSelectedItems(items.map((item) => item.uuid));
    };

    const onRequestPublish = () => {
        const data = {
            forced: true,
            published_at: fromNow().toUTC().toFormat(SQL_DATE_TIME_FORMAT),
        };

        onRequestUpdate(data);
    };
    const onRequestUnpublish = () => {
        const data = {
            forced: true,
            published_at: null,
        };

        onRequestUpdate(data);
    };
    const onRequestUpdate = (data) => {
        const id = toast.loading("Uodating...");

        const promises = selectedItems.map((uuid) =>
            apiPut(`/v4/container/${uuid}`, auth.publication, data)
        );
        Promise.all(promises)
            .then(() => {
                toast.success("Container updated", { id });
                loadContainers();
            })
            .catch((err) => {
                toast.dismiss(id);
                modal.error({
                    title: "Unable to update container",
                    message: messageFromException(err),
                });
            });
    };

    const onRequestDelete = () => {
        modal.warning({
            title: "Delete Items",
            children: (
                <Fragment>
                    Are you sure you want to delete the selected items? This
                    cannot be undone.
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={handleDelete}>Delete</DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const handleDelete = async () => {
        const id = toast.loading("Deleting...");
        const promises = selectedItems.map((uuid) =>
            apiDel(`/v4/container/${uuid}`, auth.publication)
        );
        return Promise.all(promises)
            .then(() => {
                toast.success("Container deleted", { id });
                modal.dismiss();
                loadContainers();
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Unable to delete container",
                    message: messageFromException(err),
                });
            });
    };

    const onKeyDown = (e) => {
        if (e.key !== "Enter") {
            return;
        }
        onSearchClick();
    };
    const onSearchClick = () => {
        const val = inputRef.current.value;
        if (!val || !val.length) {
            updateParams("keyword", null);
        } else {
            updateParams("keyword", val);
        }
    };

    useEffect(() => {
        setCurrentNav("container");
    }, [setCurrentNav]);

    useEffect(() => {
        if (selectedItems.length) {
            setToolbarButtonsEnabled(true);
            return;
        }
        setToolbarButtonsEnabled(false);
    }, [selectedItems]);

    useEffect(() => {
        loadContainers();
        const params = new URLSearchParams(location.search);
        setQueryParams(params);
    }, [location, loadContainers]);

    useEffect(() => {
        if (queryParams.has("type") || queryParams.has("status")) {
            setIsFiltered(true);
        } else {
            setIsFiltered(false);
        }
    }, [queryParams]);

    return (
        <MainBlock>
            <div className="sticky top-0">
                <Toolbar>
                    <ToolbarGroup>
                        <input
                            type="text"
                            ref={inputRef}
                            className="focus:outline-none relative mr-2 flex w-3/4 items-center rounded-md border border-gray-300 bg-white py-0 px-2 text-xs placeholder-gray-500 focus:border-green-500 focus:text-gray-900 focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 sm:w-auto"
                            placeholder="Search containers"
                            onKeyDown={onKeyDown}
                        />
                        <ToolbarButton
                            icon={<SearchIcon />}
                            onClick={onSearchClick}
                        />
                    </ToolbarGroup>
                    <ToolbarGroup>
                        <ToolbarButton
                            icon={<RefreshIcon />}
                            onClick={loadContainers}
                        />
                    </ToolbarGroup>
                    <ToolbarGroup className="flex-grow">
                        <ToolbarButton
                            icon={<TrashIcon />}
                            disabled={!toolbarButtonsEnabled}
                            onClick={onRequestDelete}
                        >
                            Delete
                        </ToolbarButton>
                        <ToolbarButton
                            icon={<CloudUploadIcon />}
                            disabled={!toolbarButtonsEnabled}
                            onClick={onRequestPublish}
                        >
                            Publish
                        </ToolbarButton>
                        <ToolbarButton
                            icon={<CloudDownloadIcon />}
                            disabled={!toolbarButtonsEnabled}
                            onClick={onRequestUnpublish}
                        >
                            Unpublish
                        </ToolbarButton>
                        <ToolbarButton
                            className="ml-auto text-green-600"
                            icon={<SupportIcon />}
                            onClick={() => {
                                contextualHelp.showContextualHelp(
                                    "/container/index.html"
                                );
                            }}
                        >
                            Help
                        </ToolbarButton>
                    </ToolbarGroup>
                </Toolbar>
            </div>
            <div className="my-4 flex flex-wrap justify-center gap-2">
                <div>
                    <ButtonGroup
                        variant="xs"
                        buttons={[
                            <button
                                type="button"
                                key="filter-type-all"
                                selected={!queryParams.get("type")}
                                onClick={updateParams.bind(null, "type", null)}
                            >
                                All
                            </button>,
                            ...filterItems.type.map((item) => (
                                <button
                                    type="button"
                                    key={"filter-type-" + item.value}
                                    selected={
                                        queryParams.get("type") === item.value
                                    }
                                    onClick={updateParams.bind(
                                        null,
                                        "type",
                                        item.value
                                    )}
                                >
                                    {item.label}
                                </button>
                            )),
                        ]}
                    />
                </div>
                <div>
                    <ButtonGroup
                        variant="xs"
                        buttons={[
                            <button
                                type="button"
                                key="filter-status-all"
                                selected={!queryParams.get("status")}
                                onClick={updateParams.bind(
                                    null,
                                    "status",
                                    null
                                )}
                            >
                                All
                            </button>,
                            ...filterItems.status.map((item) => (
                                <button
                                    type="button"
                                    key={"filter-status-" + item.value}
                                    selected={
                                        queryParams.get("status") === item.value
                                    }
                                    onClick={updateParams.bind(
                                        null,
                                        "status",
                                        item.value
                                    )}
                                >
                                    {item.label}
                                </button>
                            )),
                        ]}
                    />
                </div>
                <div className={isFiltered ? "" : "hidden"}>
                    <ButtonGroup
                        variant="xs"
                        buttons={[
                            <button
                                type="button"
                                key="reset-filters"
                                onClick={onResetFilters}
                            >
                                Reset
                            </button>,
                        ]}
                    />
                </div>

                <div className="md:grid md:flex-grow md:justify-items-end">
                    <Pagination
                        maxPages={3}
                        variant="xs"
                        basePath="container"
                        {...paginationMeta}
                    />
                </div>
            </div>{" "}
            <div>
                <Fragment>
                    <ContainerTable
                        loading={loading}
                        items={containerItems}
                        onSelect={onSingleSelect}
                        onSelectAll={onAllSelect}
                        onSortClick={onSortClick}
                        selectedItems={selectedItems}
                        sortedColumn={
                            queryParams.has("order")
                                ? queryParams.get("order")
                                : "created_at"
                        }
                    />

                    <div className="my-4">
                        <Pagination basePath="container" {...paginationMeta} />
                    </div>
                </Fragment>
            </div>
        </MainBlock>
    );
}
